import React, { useEffect, useState } from 'react';
import { MentionsInput, Mention } from 'react-mentions';
import { useDispatch, useSelector } from 'react-redux';

import './index.css';

import {
  Avatar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button as MaterialButton,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';

import AvatarComponent from '~/components/Avatar';
import { useAuth } from '~/hooks/auth';
import api from '~/services/api';
import { IState } from '~/store';
import {
  commentTaskRequest,
  getCommentsRequest,
  updateTaskCommentRequest,
  deleteTaskCommentRequest,
} from '~/store/modules/comments/actions';
import { getUsersByGroupsRequest } from '~/store/modules/users/actions';

import Button from '../Button';
import {
  Container,
  UserAvatar,
  CommentBox,
  CommentInput,
  CommentInfo,
  CommentsEditContainer,
  Buttons,
  ButtonGhost,
  FormContainer,
} from './styles';

interface CommentProps {
  id: string;
  processInstanceId: string;
  taskId: string;
  taskName: string;
  userId: string | undefined;
  userFirstName: string | undefined;
  userLastName: string | undefined;
  userAvatar: string | undefined;
  content: string;
  created_at: Date;
}

interface CommentFormProps {
  task: any;
}

function CommentForm({ task }: CommentFormProps) {
  const { user } = useAuth();
  const [content, setContent] = useState('');
  const [data, setData] = useState<any>({});
  const [users, setUsers] = useState<any[]>([]);

  const dispatch = useDispatch();

  const usersByGroups = useSelector(
    (state: IState) => state.users.usersByGroups,
  );

  useEffect(() => {
    if (usersByGroups.length > 0) {
      const formattedUsers = usersByGroups.map((u: any) => ({
        id: u.id,
        display: `${u.firstName} ${u.lastName}`,
      }));
      setUsers(formattedUsers);
    }
  }, [usersByGroups]);

  const handleContentChange = (event: any, newValue: string) => {
    setContent(newValue);

    setData({
      processInstanceId: task.processInstanceId,
      taskId: task.id,
      userId: user.personId,
      userFirstName: user.firstName,
      userLastName: user.lastName,
      userAvatar: user.thumbnail,
      content: newValue,
      created_at: new Date(),
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (data.content) {
      const regex = /@\[(.*?)\]\((\d+)\)/g;

      const mentions = Array.from(data?.content.matchAll(regex)).map(
        (match: any) => {
          const nomeCompleto = match[1];
          const [userFirstName, ...lastNameParts] = nomeCompleto.split(' ');
          const userLastName = lastNameParts.join(' ') || '';

          return {
            userFirstName,
            userLastName,
            id: match[2],
          };
        },
      );

      data.mentions = mentions;
      data.content = data.content.replace(regex, '@$1');
    }

    dispatch(
      commentTaskRequest({
        processInstanceId: task?.rootProcessInstanceId,
        taskId: task?.id,
        taskName: task.name,
        userId: String(user.personId),
        userFirstName: user.firstName,
        userLastName: user.lastName,
        userAvatar: user.thumbnail,
        content: data.content,
      }),
    );

    if (data.mentions.length >= 1) {
      api.post('/comments/notify-mention', {
        userFirstName: user.firstName,
        userLastName: user.lastName,
        nomeSolicitacao: task.nomeSolicitacao,
        nomeTarefa: task.name,
        orgId: task.tenantId,
        comment: data.content,
        mentions: data.mentions,
      });
    }

    if (task.assignee && data.mentions.length === 0) {
      api.post('/comments/notify-assignee', {
        userId: task.assignee,
        userFirstName: user.firstName,
        userLastName: user.lastName,
        nomeSolicitacao: task.nomeSolicitacao,
        nomeTarefa: task.name,
        orgId: task.tenantId,
        comment: data.content,
      });
    }

    if (!task.assignee && data.mentions.length === 0) {
      api.post('/comments/notify-available', {
        userFirstName: user.firstName,
        userLastName: user.lastName,
        nomeSolicitacao: task.nomeSolicitacao,
        nomeTarefa: task.name,
        groups: task.candidateGroups,
        orgId: task.tenantId,
        users: task.users,
        comment: data.content,
      });
    }

    setContent('');
  };

  return (
    <form onSubmit={handleSubmit}>
      <FormContainer>
        <MentionsInput
          className="input-container"
          value={content}
          onChange={handleContentChange}
          placeholder="Adicionar comentário..."
          style={{
            width: '100%',
            height: '8rem',
            border: '1px solid  #dfe1e6 !important',
            borderRadius: '5px !important',
          }}
        >
          <Mention
            trigger="@"
            data={users}
            displayTransform={(id, display) => `@${display}`}
            renderSuggestion={(
              suggestion,
              search,
              highlightedDisplay,
              index,
              focused,
            ) => (
              <div
                style={{
                  padding: '10px',
                  cursor: 'pointer',
                  border: focused
                    ? '2px solid rgb(108, 108, 108)'
                    : '2px solid transparent',
                  borderRadius: '4px',
                }}
              >
                {highlightedDisplay}
              </div>
            )}
          />
        </MentionsInput>
        <Button disabled={content === ''} type="submit">
          Comentar
        </Button>
      </FormContainer>
    </form>
  );
}

function Comment({
  id,
  processInstanceId,
  taskId,
  taskName,
  content,
  userId,
  userFirstName,
  userLastName,
  userAvatar,
  created_at,
}: CommentProps) {
  const isCommentLoading = useSelector(
    (state: IState) => state.comments.loading,
  );

  const dispatch = useDispatch();

  const { user } = useAuth();

  const dateFormatted = new Date(created_at).toLocaleDateString('pt-br', {
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: 'numeric',
  });

  const [isEdditing, setIsEdditing] = useState(false);
  const [inputValue, setInputValue] = useState(content);
  const [open, setOpen] = React.useState(false);

  const handleClose = () => {
    setOpen(false);
  };
  const handleEditComment = () => {
    dispatch(
      updateTaskCommentRequest({
        id,
        processInstanceId,
        taskId,
        content: inputValue,
      }),
    );
    setIsEdditing(false);
  };

  const handleDeleteComment = () => {
    setOpen(false);
    dispatch(deleteTaskCommentRequest({ id, processInstanceId, taskId }));
  };

  return (
    <div>
      <CommentBox>
        <UserAvatar>
          {userAvatar ? (
            <Avatar
              src={userAvatar}
              style={{
                width: '32px',
                height: '32px',
              }}
            />
          ) : (
            <AvatarComponent
              name={`${userFirstName} ${userLastName}`}
              size={32}
            />
          )}
        </UserAvatar>
        <CommentInfo>
          <span style={{ color: '#42526e', fontWeight: 600 }}>
            {userFirstName}
          </span>
          <span>{dateFormatted}</span>-
          <span style={{ fontWeight: 'bold' }}>{taskName}</span>
          {isEdditing ? (
            <CommentsEditContainer>
              <CommentInput
                value={inputValue}
                placeholder="Editar comentario..."
                onChange={(e) => setInputValue(e.target.value)}
              />
              <Buttons>
                <Button onClick={() => handleEditComment()} type="button">
                  Salvar
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setIsEdditing(false)}
                  type="button"
                >
                  Cancelar
                </Button>
              </Buttons>
            </CommentsEditContainer>
          ) : isCommentLoading ? (
            <Skeleton animation="wave" variant="rect" height={16} width={180} />
          ) : (
            <p style={{ marginTop: '8px', padding: '0 4px' }}>{content}</p>
          )}
          {!isEdditing && user.personId === Number(userId) && (
            <Buttons>
              <ButtonGhost
                onClick={() => {
                  setIsEdditing(true);
                  setInputValue('');
                }}
                type="button"
              >
                Editar
              </ButtonGhost>
              <ButtonGhost onClick={() => setOpen(true)} type="button">
                Excluir
              </ButtonGhost>
            </Buttons>
          )}
        </CommentInfo>
      </CommentBox>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Excluir este comentário?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Uma vez excluído, não é possível recuperar.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MaterialButton
            style={{ background: 'rgb(235, 13, 69)', color: '#FFF' }}
            variant="contained"
            onClick={handleDeleteComment}
          >
            Excluir
          </MaterialButton>
          <MaterialButton style={{ color: '#4d4d4d' }} onClick={handleClose}>
            Cancelar
          </MaterialButton>
        </DialogActions>
      </Dialog>
    </div>
  );
}

function TaskComments({ task }: any) {
  const dispatch = useDispatch();

  const commentsList = useSelector(
    (state: IState) => state.comments.commentsList,
  );

  const groups = useSelector((state: IState) => state.users.groups);

  useEffect(() => {
    if (task) {
      dispatch(getCommentsRequest(task.rootProcessInstanceId));

      if (groups && groups.length > 0) {
        dispatch(getUsersByGroupsRequest(groups));
      }
    }
  }, [dispatch, task, groups]);

  return (
    <Container>
      <h1>Comentários</h1>
      <CommentForm task={task} />
      {commentsList?.map((comment) => (
        <Comment
          id={comment.id}
          processInstanceId={comment.processInstanceId}
          taskId={comment.taskId}
          taskName={comment.taskName}
          content={comment.content}
          created_at={comment.created_at}
          userId={comment.userId}
          userAvatar={comment.userAvatar}
          userFirstName={comment.userFirstName}
          userLastName={comment.userLastName}
        />
      ))}
    </Container>
  );
}

export default TaskComments;
